import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { homePathForRole } from '../lib/appTarget.js';
import { dismissTop } from '../lib/backStack.js';
import Modal from './Modal.jsx';
import Button from './Button.jsx';

/**
 * The Android back button, for all three apps.
 *
 * With no handler, Android leaves it to the webview: that walks back through
 * whatever history the router happened to leave (sign-in and redirects
 * *replace* entries, so there often isn't any), and when it runs out it quits
 * the app without a word. So back felt random, and one press too many threw
 * the user out.
 *
 * Now, in order:
 *   1. an open sheet, dialog or drawer closes — it is what's on top;
 *   2. on the app's home screen, it asks before leaving;
 *   3. anywhere else it goes to the previous screen, or home if there is none.
 */

/** Screens where back means "leave the app", per signed-in state. */
function homePaths(user) {
  const home = homePathForRole(user?.role);
  // Signed out, the front door is sign-in (or the onboarding / landing page
  // in the shop); signed in, it is the role's own home.
  return user ? new Set([home, '/']) : new Set(['/', '/login', '/onboard']);
}

export default function BackButton() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [asking, setAsking] = useState(false);
  const appRef = useRef(null);

  // Read the latest location inside the native listener without re-registering
  // it on every navigation.
  const state = useRef({ pathname, user, asking });
  state.current = { pathname, user, asking };

  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return undefined;
    let handle;
    import('@capacitor/app')
      .then(({ App }) => {
        appRef.current = App;
        return App.addListener('backButton', ({ canGoBack }) => {
          // The exit question is itself a layer: back while it is showing means
          // "no, stay" — it is dismissed through the same stack as any sheet.
          if (dismissTop()) return;

          const { pathname: here, user: who } = state.current;
          if (homePaths(who).has(here)) {
            setAsking(true);
            return;
          }
          // A deep link or a replaced redirect can leave nothing behind us;
          // then back goes home rather than out of the app.
          if (canGoBack && window.history.length > 1) navigate(-1);
          else navigate(who ? homePathForRole(who.role) : '/', { replace: true });
        });
      })
      .then((h) => { handle = h; })
      .catch(() => {});
    return () => handle?.remove?.();
  }, [navigate]);

  return (
    <Modal
      open={asking}
      onClose={() => setAsking(false)}
      title="Leave HealthyFruitsBox?"
      description={
        // Only true once there is a session to keep; on the sign-in screen it
        // would promise something that isn't there.
        user ? "You can come back any time — you'll stay signed in." : 'You can come back any time.'
      }
      size="sm"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={() => setAsking(false)}>
            Stay
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              setAsking(false);
              appRef.current?.exitApp?.();
            }}
          >
            Exit
          </Button>
        </div>
      }
    />
  );
}
